/**
 * Environment Utilities
 *
 * Utilitários para detecção e manipulação de ambientes da aplicação
 */

export type Environment = 'development' | 'staging' | 'production'

export interface EnvironmentInfo {
  environment: Environment
  label: string
  isProduction: boolean
  isDevelopment: boolean
  isStaging: boolean
}

export interface EnvironmentColors {
  backgroundColor: string
  borderColor: string
  textColor: string
}

/**
 * Detecta o ambiente atual baseado no hostname ou variável de ambiente
 *
 * @returns {Environment} O ambiente detectado
 */
export function detectEnvironment(): Environment {
  const hostname = window.location.hostname.toLowerCase()

  // Verifica variável de ambiente primeiro (se disponível)
  if (process.env.VUE_APP_ENVIRONMENT) {
    const env = process.env.VUE_APP_ENVIRONMENT.toLowerCase()
    if (['development', 'staging', 'production'].includes(env)) {
      return env as Environment
    }
  }

  // Detecta pelo hostname
  if (hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.includes('app-des.securamdata.com')) {
    return 'development'
  }

  if (hostname.includes('app-hmg.securamdata.com')) {
    return 'staging'
  }

  if (hostname.includes('app.securamdata.com')) {
    return 'production'
  }

  // Fallback: se não conseguir detectar, assume desenvolvimento
  return 'development'
}

/**
 * Retorna informações completas sobre o ambiente atual
 *
 * @returns {EnvironmentInfo} Informações do ambiente
 */
export function getEnvironmentInfo(): EnvironmentInfo {
  const environment = detectEnvironment()

  return {
    environment,
    label: getEnvironmentLabel(environment),
    isProduction: environment === 'production',
    isDevelopment: environment === 'development',
    isStaging: environment === 'staging'
  }
}

/**
 * Retorna o label do ambiente
 *
 * @param {Environment} environment - O ambiente
 * @returns {string} O label do ambiente
 */
export function getEnvironmentLabel(environment: Environment): string {
  const labels: Record<Environment, string> = {
    development: 'DEV',
    staging: 'HMG',
    production: 'PROD'
  }

  return labels[environment] || 'DEV'
}

/**
 * Retorna as classes CSS para o ambiente
 *
 * @param {Environment} environment - O ambiente
 * @returns {Record<string, boolean>} Objeto com classes CSS
 */
export function getEnvironmentClasses(environment: Environment): Record<string, boolean> {
  return {
    'env-dev': environment === 'development',
    'env-staging': environment === 'staging',
    'env-prod': environment === 'production',
    'navbar-env-dev': environment === 'development',
    'navbar-env-staging': environment === 'staging',
    'navbar-env-prod': environment === 'production'
  }
}

/**
 * Retorna as cores do ambiente para uso em estilos
 *
 * @param {Environment} environment - O ambiente
 * @param {boolean} isDarkTheme - Se o tema é escuro
 * @returns {EnvironmentColors} Cores do ambiente
 */
export function getEnvironmentColors(
  environment: Environment,
  isDarkTheme: boolean = false
): EnvironmentColors {
  if (environment === 'production') {
    return {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      textColor: 'inherit'
    }
  }

  if (environment === 'development') {
    return {
      backgroundColor: isDarkTheme
        ? 'rgba(34, 197, 94, 0.12)'
        : 'rgba(34, 197, 94, 0.08)',
      borderColor: isDarkTheme
        ? 'rgba(34, 197, 94, 0.3)'
        : 'rgba(34, 197, 94, 0.2)',
      textColor: isDarkTheme ? '#4ade80' : '#16a34a'
    }
  }

  if (environment === 'staging') {
    return {
      backgroundColor: isDarkTheme
        ? 'rgba(251, 191, 36, 0.12)'
        : 'rgba(251, 191, 36, 0.08)',
      borderColor: isDarkTheme
        ? 'rgba(251, 191, 36, 0.3)'
        : 'rgba(251, 191, 36, 0.2)',
      textColor: isDarkTheme ? '#fbbf24' : '#d97706'
    }
  }

  return {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    textColor: 'inherit'
  }
}

/**
 * Retorna o estilo inline para a navbar baseado no ambiente
 *
 * @param {Environment} environment - O ambiente
 * @param {boolean} isDarkTheme - Se o tema é escuro
 * @returns {Record<string, string>} Objeto de estilos
 */
export function getEnvironmentNavbarStyle(
  environment: Environment,
  isDarkTheme: boolean = false
): Record<string, string> {
  if (environment === 'production') {
    return {}
  }

  const colors = getEnvironmentColors(environment, isDarkTheme)

  return {
    backgroundColor: colors.backgroundColor,
    borderBottom: `2px solid ${colors.borderColor}`
  }
}

/**
 * Retorna as classes CSS para a navbar baseado no ambiente
 *
 * @param {Environment} environment - O ambiente
 * @returns {Record<string, boolean>} Objeto com classes CSS
 */
export function getEnvironmentNavbarClasses(environment: Environment): Record<string, boolean> {
  return {
    'navbar-env-dev': environment === 'development',
    'navbar-env-staging': environment === 'staging',
    'navbar-env-prod': environment === 'production'
  }
}

